import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react'
import { Copy, ExternalLink, Globe, Unlink } from 'lucide-react'
import { Button } from '@/components/shadcn/button'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/shadcn/hover-card'
import { findLinks, linkHost, shortLink } from './links'

export type EditorHandle = {
  /** Type text at the end, as if pasted (links in it become chips). */
  insert: (text: string) => void
  clear: () => void
  focus: () => void
}

type Props = {
  placeholder: string
  disabled?: boolean
  onChange: (text: string) => void
  onSubmit: () => void
  onEscape?: () => void
  onPasteFiles: (files: File[]) => void
  /** Sees each key first; true means it was handled (a menu above the box took it). */
  onKey?: (e: React.KeyboardEvent) => boolean
}

const PILL = 'mx-px inline rounded-xs bg-accent/15 px-1 py-px font-medium text-accent-hover underline decoration-accent/40 decoration-dotted underline-offset-2 cursor-pointer'

/** The message as Claude gets it: a link chip is its full URL again. */
function readText(el: Node): string {
  let s = ''
  el.childNodes.forEach((n) => {
    if (n.nodeType === Node.TEXT_NODE) s += (n as Text).data
    else if (n instanceof HTMLElement) {
      if (n.dataset.url) s += n.dataset.url
      else if (n.tagName === 'BR') s += '\n'
      else { if (n.tagName === 'DIV' && s && !s.endsWith('\n')) s += '\n'; s += readText(n) }
    }
  })
  return s
}

function pill(url: string): HTMLSpanElement {
  const el = document.createElement('span')
  el.contentEditable = 'false'
  el.dataset.url = url
  el.className = PILL
  el.textContent = shortLink(url)
  return el
}

/**
 * Turn finished links into chips. A link is finished once something follows
 * it, or always when `all` (a paste); the one still being typed is left alone
 * so the chip never swallows half a URL.
 */
function linkify(root: HTMLElement, all: boolean): void {
  const sel = window.getSelection()
  for (;;) {
    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: (n) => ((n.parentElement?.closest('[data-url]')) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
    })
    let changed = false
    for (let n = walker.nextNode() as Text | null; n; n = walker.nextNode() as Text | null) {
      const caretHere = sel?.anchorNode === n ? sel.anchorOffset : -1
      const m = findLinks(n.data).find((l) => all || l.rawEnd < n!.data.length)
      if (!m) continue
      const urlNode = n.splitText(m.start)
      const rest = urlNode.splitText(m.url.length)
      urlNode.replaceWith(pill(m.url))
      // Leave room after a chip at the very end so the caret has somewhere to go.
      const restWasEmpty = !rest.data
      if (restWasEmpty) rest.data = ' '
      if (caretHere >= m.end) sel?.collapse(rest, restWasEmpty ? 1 : caretHere - m.end)
      changed = true
      break
    }
    if (!changed) return
  }
}

/**
 * Where a message is written: plain text, except that links show as short
 * chips (full link on hover, with a way to open it). Content-editable rather
 * than a textarea because a textarea cannot shorten or highlight part of its text.
 */
export const LinkEditor = forwardRef<EditorHandle, Props>(function LinkEditor({ placeholder, disabled, onChange, onSubmit, onEscape, onPasteFiles, onKey }, ref) {
  const el = useRef<HTMLDivElement>(null)
  const [empty, setEmpty] = useState(true)
  const [hover, setHover] = useState<{ url: string; rect: DOMRect; node: HTMLElement } | null>(null)
  const closeT = useRef<ReturnType<typeof setTimeout>>(undefined)

  const changed = () => {
    const root = el.current!
    const text = readText(root)
    // A lone <br> the browser leaves behind would hide the placeholder.
    if (!text.trim() && !root.querySelector('[data-url]')) root.innerHTML = ''
    setEmpty(!root.childNodes.length)
    onChange(text)
  }

  const typeText = (text: string) => {
    document.execCommand('insertText', false, text)
    linkify(el.current!, true)
    changed()
  }

  useImperativeHandle(ref, () => ({
    insert: (text) => {
      const root = el.current!
      root.focus()
      const sel = window.getSelection()!
      sel.selectAllChildren(root)
      sel.collapseToEnd()
      typeText(text)
    },
    clear: () => { el.current!.innerHTML = ''; setHover(null); changed() },
    focus: () => el.current?.focus(),
  }))

  useEffect(() => () => clearTimeout(closeT.current), [])

  const keepOpen = () => clearTimeout(closeT.current)
  const closeSoon = () => { clearTimeout(closeT.current); closeT.current = setTimeout(() => setHover(null), 180) }

  return (
    <div className="relative">
      <div ref={el} role="textbox" aria-multiline="true" aria-label="Message" aria-placeholder={placeholder}
        contentEditable={!disabled} suppressContentEditableWarning
        className="max-h-60 min-h-[52px] overflow-y-auto whitespace-pre-wrap break-words px-4 pb-2 pt-3.5 text-sm leading-relaxed outline-none focus:outline-none focus-visible:outline-none"
        style={{ outline: 'none' }}
        onInput={() => { linkify(el.current!, false); changed() }}
        onKeyDown={(e) => {
          if (onKey?.(e)) { e.preventDefault(); return }
          if (e.key === 'Enter' && !e.nativeEvent.isComposing) {
            e.preventDefault()
            if (e.shiftKey || e.altKey) { document.execCommand('insertLineBreak'); changed() } else { linkify(el.current!, true); changed(); onSubmit() }
          }
          if (e.key === 'Escape' && onEscape) { e.preventDefault(); onEscape() }
        }}
        onPaste={(e) => {
          e.preventDefault()
          const files = [...e.clipboardData.files]
          if (files.length) return onPasteFiles(files)
          const text = e.clipboardData.getData('text/plain')
          if (text) typeText(text)
        }}
        // Dropping text or a file straight on the editor is the pane's job (it attaches files).
        onDrop={(e) => { if (e.dataTransfer.types.includes('Files')) e.preventDefault() }}
        onClick={(e) => {
          const p = (e.target as HTMLElement).closest<HTMLElement>('[data-url]')
          if (p && (e.metaKey || e.ctrlKey)) window.open(p.dataset.url, '_blank', 'noopener,noreferrer')
        }}
        onMouseOver={(e) => {
          const p = (e.target as HTMLElement).closest<HTMLElement>('[data-url]')
          if (p) { keepOpen(); setHover({ url: p.dataset.url!, rect: p.getBoundingClientRect(), node: p }) }
        }}
        onMouseOut={(e) => { if ((e.target as HTMLElement).closest('[data-url]')) closeSoon() }}
      />
      {empty && <div aria-hidden className="pointer-events-none absolute left-4 top-3.5 text-sm leading-relaxed text-fg-faint">{placeholder}</div>}
      {hover && (
        <HoverCard open onOpenChange={(o) => { if (!o) closeSoon() }}>
          <HoverCardTrigger asChild>
            <span aria-hidden className="pointer-events-none fixed" style={{ left: hover.rect.left, top: hover.rect.top, width: hover.rect.width, height: hover.rect.height }} />
          </HoverCardTrigger>
          <LinkCard url={hover.url} onEnter={keepOpen} onLeave={closeSoon}
            onUnlink={() => { hover.node.replaceWith(document.createTextNode(hover.url)); setHover(null); changed() }} />
        </HoverCard>
      )}
    </div>
  )
})

function LinkCard({ url, onEnter, onLeave, onUnlink }: { url: string; onEnter: () => void; onLeave: () => void; onUnlink: () => void }) {
  const host = linkHost(url)
  const [icon, setIcon] = useState(true)
  return (
    <HoverCardContent side="top" align="start" className="w-80 p-3" onPointerEnter={onEnter} onPointerLeave={onLeave}>
      <div className="flex items-start gap-2.5">
        <div className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-md border border-border bg-surface-3">
          {icon ? <img src={`https://${host}/favicon.ico`} alt="" className="size-4" onError={() => setIcon(false)} /> : <Globe size={15} className="text-fg-subtle" />}
        </div>
        <div className="min-w-0 flex-1">
          <div className="truncate text-sm font-medium">{host}</div>
          <a href={url} target="_blank" rel="noopener noreferrer" className="line-clamp-3 break-all font-mono text-[11px] text-fg-subtle hover:text-accent-hover hover:underline">{url}</a>
        </div>
      </div>
      <div className="mt-3 flex items-center gap-1.5">
        <Button asChild size="xs"><a href={url} target="_blank" rel="noopener noreferrer"><ExternalLink />Open</a></Button>
        <Button size="xs" variant="ghost" onClick={() => void navigator.clipboard.writeText(url)}><Copy />Copy</Button>
        <Button size="xs" variant="ghost" className="ml-auto text-fg-subtle" onClick={onUnlink}><Unlink />Plain text</Button>
      </div>
    </HoverCardContent>
  )
}
